import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { teamsAPI } from '@/lib/api';
import { Building2, Target, Globe, Plus, X, CheckCircle, Users, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const teamSchema = z.object({
  name: z.string().min(2, 'Agency name must be at least 2 characters').max(100, 'Agency name must not exceed 100 characters'),
  industry: z.string().optional(),
  description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
  website: z.string().url('Please enter a valid website URL').optional().or(z.literal('')),
  linkedinUrl: z.string().url('Please enter a valid LinkedIn URL').optional().or(z.literal('')),
  companySize: z.string().optional(),
  minBudget: z.number().min(0, 'Budget must be positive').optional(),
  maxBudget: z.number().min(0, 'Budget must be positive').optional(),
});

type TeamFormData = z.infer<typeof teamSchema>;

const ONBOARDING_STEPS = [
  {
    title: 'Agency Information',
    description: 'Tell us about your agency to get started',
    icon: Building2,
  },
  {
    title: 'Services & Markets',
    description: 'Define your services and target markets',
    icon: Target,
  },
  {
    title: 'Team Setup Complete',
    description: 'Review and complete your setup',
    icon: CheckCircle,
  },
];

export function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [services, setServices] = useState<string[]>([]);
  const [serviceInput, setServiceInput] = useState('');
  const [targetMarkets, setTargetMarkets] = useState<string[]>([]);
  const [marketInput, setMarketInput] = useState('');
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<TeamFormData>({
    resolver: zodResolver(teamSchema),
    mode: 'onChange',
  });
  
  // Check if user already has teams
  const { data: teamsResponse, isLoading: teamsLoading, error: teamsError } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsAPI.getTeams(),
    retry: false,
  });

  const createTeamMutation = useMutation({
    mutationFn: async (data: TeamFormData) => {
      const teamData = {
        name: data.name,
        industry: data.industry || undefined,
        description: data.description || undefined,
        website: data.website || undefined,
        linkedinUrl: data.linkedinUrl || undefined,
        companySize: data.companySize || undefined,
        minBudget: data.minBudget || undefined,
        maxBudget: data.maxBudget || undefined,
        services: services.length > 0 ? services : undefined,
        targetMarkets: targetMarkets.length > 0 ? targetMarkets : undefined,
      };
      return teamsAPI.createTeam(teamData);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast({
        title: 'Team Created Successfully!',
        description: 'Welcome to LeadGPT. Your team is ready to go.',
      });
      navigate('/dashboard', { replace: true });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error Creating Team',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Redirect to dashboard if user already has teams
  useEffect(() => {
    if (teamsResponse?.data && Array.isArray(teamsResponse.data) && teamsResponse.data.length > 0) {
      toast({
        title: 'Welcome back!',
        description: 'You already have a team set up.',
      });
      navigate('/dashboard', { replace: true });
    }
  }, [teamsResponse, navigate, toast]);
  
  // Handle error case - allow onboarding to continue
  if (teamsError && !teamsLoading) {
    console.warn('Could not check existing teams, proceeding with onboarding:', teamsError);
  }

  // Show loading while checking teams
  if (teamsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="shadow-xl">
            <CardContent className="flex items-center justify-center p-8">
              <div className="text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground">Checking your account...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = (data: TeamFormData) => {
    if (currentStep === ONBOARDING_STEPS.length - 1) {
      createTeamMutation.mutate(data);
    } else {
      handleNext();
    }
  };

  const addService = () => {
    if (serviceInput.trim() && !services.includes(serviceInput.trim())) {
      setServices([...services, serviceInput.trim()]);
      setServiceInput('');
    }
  };

  const removeService = (serviceToRemove: string) => {
    setServices(services.filter(service => service !== serviceToRemove));
  };

  const addMarket = () => {
    if (marketInput.trim() && !targetMarkets.includes(marketInput.trim())) {
      setTargetMarkets([...targetMarkets, marketInput.trim()]);
      setMarketInput('');
    }
  };

  const removeMarket = (marketToRemove: string) => {
    setTargetMarkets(targetMarkets.filter(market => market !== marketToRemove));
  };

  const industries = [
    'Digital Marketing', 'Web Development', 'Consulting', 'SaaS', 'E-commerce',
    'Healthcare', 'Finance', 'Education', 'Real Estate', 'Manufacturing', 'Other'
  ];

  const companySizes = [
    '1-10 employees', '11-50 employees', '51-200 employees', 
    '201-500 employees', '501-1000 employees', '1000+ employees'
  ];

  const isStepValid = () => {
    if (currentStep === 0) {
      const name = watch('name');
      return name && name.length >= 2;
    }
    if (currentStep === 1) {
      return true; // Services and markets are optional
    }
    return true;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">Let's set up your agency</h3>
              <p className="text-sm text-muted-foreground">
                Tell us about your agency to personalize your experience
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Agency Name *</Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="Your Agency Name"
                />
                {errors.name && (
                  <p className="text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Select onValueChange={(value) => setValue('industry', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companySize">Team Size</Label>
                <Select onValueChange={(value) => setValue('companySize', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your team size" />
                  </SelectTrigger>
                  <SelectContent>
                    {companySizes.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Agency Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Briefly describe your agency and what you do..."
                  rows={3}
                />
                {errors.description && (
                  <p className="text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    {...register('website')}
                    placeholder="https://yourwebsite.com"
                  />
                  {errors.website && (
                    <p className="text-sm text-red-600">{errors.website.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
                  <Input
                    id="linkedinUrl"
                    {...register('linkedinUrl')}
                    placeholder="https://linkedin.com/company/yourcompany"
                  />
                  {errors.linkedinUrl && (
                    <p className="text-sm text-red-600">{errors.linkedinUrl.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">Services & Target Markets</h3>
              <p className="text-sm text-muted-foreground">
                Help us understand your offerings and ideal clients
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <Label>Services You Offer</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {services.map((service) => (
                    <Badge key={service} variant="secondary" className="flex items-center gap-1">
                      {service}
                      <button
                        type="button"
                        onClick={() => removeService(service)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={serviceInput}
                    onChange={(e) => setServiceInput(e.target.value)}
                    placeholder="e.g., Web Development, SEO, PPC"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addService}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <Label>Target Markets</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {targetMarkets.map((market) => (
                    <Badge key={market} variant="secondary" className="flex items-center gap-1">
                      {market}
                      <button
                        type="button"
                        onClick={() => removeMarket(market)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={marketInput}
                    onChange={(e) => setMarketInput(e.target.value)}
                    placeholder="e.g., Small Business, SaaS, E-commerce"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMarket())}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addMarket}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minBudget">Minimum Project Budget ($)</Label>
                  <Input
                    id="minBudget"
                    type="number"
                    min="0"
                    {...register('minBudget', { valueAsNumber: true })}
                    placeholder="1000"
                  />
                  {errors.minBudget && (
                    <p className="text-sm text-red-600">{errors.minBudget.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxBudget">Maximum Project Budget ($)</Label>
                  <Input
                    id="maxBudget"
                    type="number"
                    min="0"
                    {...register('maxBudget', { valueAsNumber: true })}
                    placeholder="50000"
                  />
                  {errors.maxBudget && (
                    <p className="text-sm text-red-600">{errors.maxBudget.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold">Ready to Launch!</h3>
              <p className="text-sm text-muted-foreground">
                Your team setup is complete. Let's create your workspace.
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">{watch('name') || 'Your Agency'}</p>
                  <p className="text-sm text-muted-foreground">{watch('industry') || 'Industry not specified'}</p>
                </div>
              </div>
              
              {services.length > 0 && (
                <div className="flex items-start gap-3">
                  <Target className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Services</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {services.slice(0, 3).map((service) => (
                        <Badge key={service} variant="outline" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                      {services.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{services.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">Team Owner</p>
                  <p className="text-sm text-muted-foreground">You'll have full access to all features</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">What's Next?</h4>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>• Connect your Gmail account for email campaigns</li>
                <li>• Import or add your first leads</li>
                <li>• Set up email templates</li>
                <li>• Invite team members</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <Card className="shadow-xl">
            <CardHeader>
              <div className="space-y-4">
                <div className="text-center">
                  <h1 className="text-2xl font-bold">Welcome to LeadGPT</h1>
                  <p className="text-muted-foreground">Let's set up your team in just a few steps</p>
                </div>
                
                <div className="space-y-2">
                  <Progress value={progress} className="w-full" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Step {currentStep + 1} of {ONBOARDING_STEPS.length}</span>
                    <span>{Math.round(progress)}% complete</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {createTeamMutation.error && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      Failed to create team: {(createTeamMutation.error as Error).message}
                    </AlertDescription>
                  </Alert>
                )}

                {renderStepContent()}
                
                <div className="flex justify-between pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={currentStep === 0}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button
                    type="submit"
                    disabled={!isStepValid() || createTeamMutation.isPending}
                    className="flex items-center gap-2"
                  >
                    {currentStep === ONBOARDING_STEPS.length - 1 ? (
                      createTeamMutation.isPending ? (
                        'Creating Team...'
                      ) : (
                        'Create Team'
                      )
                    ) : (
                      <>
                        Next
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}